import React, { useState } from 'react';
import {
  X,
  Search,
  Database,
  FileText,
  Activity,
  Pill,
  Plus,
  Check
} from 'lucide-react';

export interface HealthDataItem {
  id: string;
  category: 'lab_result' | 'vitals' | 'medication' | 'condition';
  name: string;
  date: Date;
  value?: string;
  unit?: string;
  description: string;
  status?: string;
}

interface HealthDataSharingProps {
  onClose: () => void;
  onSelect: (items: HealthDataItem[]) => void;
}

/**
 * HealthDataSharing component provides a modal for selecting
 * and sharing health data in messages to healthcare providers
 */
const HealthDataSharing: React.FC<HealthDataSharingProps> = ({
  onClose,
  onSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<HealthDataItem[]>([]);
  
  // Mock health data - in a real app, this would come from the patient record
  const healthData: HealthDataItem[] = [
    // Lab Results
    {
      id: 'lab-001',
      category: 'lab_result',
      name: 'Hemoglobin A1C',
      date: new Date(2023, 4, 15),
      value: '5.8',
      unit: '%',
      description: 'Blood glucose test for diabetes monitoring',
      status: 'normal'
    },
    {
      id: 'lab-002',
      category: 'lab_result',
      name: 'Cholesterol - LDL',
      date: new Date(2023, 4, 15),
      value: '142',
      unit: 'mg/dL',
      description: 'Low-density lipoprotein cholesterol test',
      status: 'high'
    },
    {
      id: 'lab-003',
      category: 'lab_result',
      name: 'Complete Blood Count',
      date: new Date(2023, 3, 10),
      description: 'Full blood panel with multiple metrics',
      status: 'normal'
    },
    
    // Vitals
    {
      id: 'vital-001',
      category: 'vitals',
      name: 'Blood Pressure',
      date: new Date(2023, 5, 2),
      value: '124/78',
      unit: 'mmHg',
      description: 'Last measured blood pressure',
      status: 'normal'
    },
    {
      id: 'vital-002',
      category: 'vitals',
      name: 'Heart Rate',
      date: new Date(2023, 5, 2),
      value: '72',
      unit: 'bpm',
      description: 'Resting heart rate',
      status: 'normal'
    },
    {
      id: 'vital-003',
      category: 'vitals',
      name: 'Weight',
      date: new Date(2023, 5, 2),
      value: '175',
      unit: 'lbs',
      description: 'Current weight measurement',
      status: 'normal'
    },
    
    // Medications
    {
      id: 'med-001',
      category: 'medication',
      name: 'Lisinopril',
      date: new Date(2023, 5, 1),
      value: '10mg daily',
      description: 'For blood pressure management',
    },
    {
      id: 'med-002',
      category: 'medication',
      name: 'Metformin',
      date: new Date(2023, 5, 1),
      value: '500mg twice daily',
      description: 'For blood glucose management',
    },
    
    // Conditions
    {
      id: 'cond-001',
      category: 'condition',
      name: 'Type 2 Diabetes',
      date: new Date(2022, 2, 15),
      description: 'Diagnosed 2022, managed with medication and lifestyle',
    },
    {
      id: 'cond-002',
      category: 'condition',
      name: 'Hypertension',
      date: new Date(2021, 8, 3),
      description: 'Well-controlled with medication',
    }
  ];
  
  // Filter health data based on search and category
  const filteredData = healthData.filter(item => {
    const matchesSearch = searchQuery.trim() === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesCategory = selectedCategory === null || 
      item.category === selectedCategory;
      
    return matchesSearch && matchesCategory;
  });
  
  const handleToggleItem = (item: HealthDataItem) => {
    const isSelected = selectedItems.some(selected => selected.id === item.id);
    
    if (isSelected) {
      setSelectedItems(selectedItems.filter(selected => selected.id !== item.id));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };
  
  const handleShareSelected = () => {
    onSelect(selectedItems);
    onClose();
  };
  
  const categories = [
    { id: 'lab_result', name: 'Lab Results', icon: FileText },
    { id: 'vitals', name: 'Vitals', icon: Activity },
    { id: 'medication', name: 'Medications', icon: Pill },
    { id: 'condition', name: 'Conditions', icon: Database }
  ];
  
  const getCategoryName = (category: string): string => {
    const found = categories.find(c => c.id === category);
    return found ? found.name : 'Unknown';
  };
  
  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'high':
      case 'critical':
        return 'text-[var(--status-error)]';
      case 'low':
        return 'text-[var(--status-warning)]';
      case 'normal':
        return 'text-[var(--status-success)]';
      default:
        return 'text-[var(--text-secondary)]';
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--background-surface)] rounded-lg w-full max-w-3xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border-subtle)]">
          <h2 className="font-medium text-lg text-[var(--text-primary)] flex items-center">
            <Database size={20} className="mr-2" />
            Share Health Data
          </h2>
          <button 
            onClick={onClose}
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Search and filters */}
        <div className="p-4 border-b border-[var(--border-subtle)]">
          <div className="relative mb-4">
            <input
              type="text"
              placeholder="Search your health data..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--background-elevated)] text-[var(--text-primary)]"
            />
            <Search 
              className="absolute left-3 top-2.5 h-5 w-5 text-[var(--text-secondary)]" 
            />
          </div>
          
          <div className="flex overflow-x-auto gap-2 pb-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`
                px-3 py-2 rounded-lg whitespace-nowrap
                ${selectedCategory === null 
                  ? 'bg-[var(--brand-primary)] text-white' 
                  : 'bg-[var(--background-elevated)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'
                }
              `}
            >
              All Categories
            </button>
            
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`
                  px-3 py-2 rounded-lg flex items-center whitespace-nowrap
                  ${selectedCategory === category.id 
                    ? 'bg-[var(--brand-primary)] text-white' 
                    : 'bg-[var(--background-elevated)] text-[var(--text-secondary)] hover:bg-[var(--background-subtle)]'
                  }
                `}
              >
                <category.icon size={16} className="mr-1" />
                {category.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Health data list */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredData.length === 0 ? (
            <div className="text-center py-8">
              <Database size={40} className="mx-auto text-[var(--text-disabled)] mb-2" />
              <p className="text-[var(--text-secondary)]">
                {searchQuery.trim() !== '' 
                  ? 'No health data matches your search' 
                  : 'No health data available in this category'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredData.map(item => {
                const isSelected = selectedItems.some(selected => selected.id === item.id);
                
                return (
                  <div 
                    key={item.id}
                    onClick={() => handleToggleItem(item)}
                    className={`
                      p-3 rounded-lg border cursor-pointer
                      ${isSelected 
                        ? 'border-[var(--brand-primary)] bg-[var(--brand-primary-subtle)]' 
                        : 'border-[var(--border-subtle)] bg-[var(--background-elevated)] hover:bg-[var(--background-surface-hover)]'
                      }
                    `}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center">
                          <span className="text-xs px-2 py-0.5 rounded bg-[var(--background-subtle)] text-[var(--text-secondary)] mr-2">
                            {getCategoryName(item.category)}
                          </span>
                          {item.status && (
                            <span className={`text-xs ${getStatusColor(item.status)}`}>
                              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                            </span>
                          )}
                        </div>
                        
                        <h3 className="font-medium text-[var(--text-primary)] mt-1">
                          {item.name}
                        </h3>
                        
                        {item.value && (
                          <div className="text-sm mt-1">
                            <span className="font-medium">
                              {item.value}
                            </span>
                            {item.unit && (
                              <span className="text-[var(--text-secondary)]"> {item.unit}</span>
                            )}
                          </div>
                        )}
                        
                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                          {item.description}
                        </p>
                        
                        <p className="text-xs text-[var(--text-secondary)] mt-2">
                          {item.date.toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className={`
                        h-6 w-6 rounded-full border flex-shrink-0
                        ${isSelected 
                          ? 'bg-[var(--brand-primary)] border-[var(--brand-primary)]' 
                          : 'border-[var(--border-subtle)] bg-[var(--background-surface)]'
                        }
                        flex items-center justify-center
                      `}>
                        {isSelected && (
                          <Check size={14} className="text-white" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer with actions */}
        <div className="border-t border-[var(--border-subtle)] p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-[var(--text-secondary)]">
              {selectedItems.length} item{selectedItems.length !== 1 && 's'} selected
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-[var(--border-subtle)] rounded-lg text-[var(--text-primary)] hover:bg-[var(--background-surface-hover)]"
              >
                Cancel
              </button>
              
              <button
                onClick={handleShareSelected}
                disabled={selectedItems.length === 0}
                className={`
                  px-4 py-2 rounded-lg flex items-center
                  ${selectedItems.length > 0 
                    ? 'bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-hover)]' 
                    : 'bg-[var(--background-subtle)] text-[var(--text-disabled)]'
                  }
                `}
              >
                <Plus size={16} className="mr-2" />
                Share Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthDataSharing; 